// 設備 › 庫存與開通（/devices/inventory，舊 /inventory 轉址；SPEC #vms-web-inventory DEV-15、#vms-web-detected DEV-16；Eddy 2026-10-05；
//   VMS UX 評審 2026-10-13 P0-4：成為設備頁的分頁；組織 = 頁首範圍所在的組織，頁內不另放組織下拉）。
//   ?claim=1：設備頁〔新增 ▾〕的「用序號認領」直接打開認領對話框。
//   - 名稱欄：庫存 API 沒有設備顯示名（加入家戶前沒有名字），先只列機種；已加入的連到設備頁（推論，SPEC 的「名稱」欄待後端帶 display_name）
//   - 範圍是整個組織（範圍切換器選到 site 時預設篩那個 site 的指派 / 綁定）；權限 = 組織 owner / 管理員或 org.devices；沒有任何家戶也出現
//   - 未歸戶設備：機種、名稱、序號（沒有就顯示尾碼；畫面不出現 device_id / AID，UX-09）、狀態、最近上線、指派或所在的家戶；認領、指派、取消指派
//   - 偵測到的設備：以場域為範圍，列現場發現、還沒綁定的設備；庫存內一鍵收編
// DEV-15 已接 app-api /v1/orgs/{org}/inventory…（platform!252）：篩選（狀態 / 機種 / 節點 / 序號）、多選指派 / 取消指派、認領（可貼多列）。
// 還沒做：〔出貨登記〕〔釋出〕（後端未開放，Eddy 確認前）。DEV-16 偵測到的設備在 DetectedPanel.tsx。
import { useMemo, useState } from "react";
import {
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useScope } from "../../lib/scope";
import {
  hasOrgPerm,
  INVENTORY_API_READY,
  inventoryApi,
  orgUsable,
  type InventoryDevice,
  type MyOrg,
} from "../../lib/planeApi";
import { gateOrg, OrgCapsule, OrgChooser } from "../shell/OrgGate";
import { errText } from "../settings/OrgSection";
import { PairDialog } from "../devices/PairDialog";
import { homeTargets } from "../orgtree/homeTargets";
import { AssignDialog, ClaimDialog, UnassignDialog } from "./InventoryDialogs";
import { selectionActions } from "./inventoryModel";
import { DetectedPanel } from "./DetectedPanel";
import { NodeEnrollPanel } from "./NodeEnrollPanel";
import { useNodeEnrollOrgs } from "./useNodeEnrollOrgs";
import { stamp } from '../../lib/timefmt';

/** 序號，沒有就用尾碼（device_ref 末 6 碼，同 App 的「尾碼」）。 */
export function serialOrTail(
  d: Pick<InventoryDevice, "serial_no" | "device_ref">,
): string {
  return d.serial_no || "…" + d.device_ref.slice(-6);
}

export function InventoryPage() {
  const { t } = useTranslation();
  const { org, orgs } = useScope();
  const loc = useLocation();
  const managed = orgs.filter(
    (o) => orgUsable(o) && hasOrgPerm(o, "org.devices"),
  );
  // 〔Kinio Node 開通〕（DEV-17）：node_enroll 的組織、而且有額度或開通過；只有這個分頁的人（node_admin）直接到這裡
  const enrollOrgs = useNodeEnrollOrgs(orgs);
  const onEnroll = loc.pathname.startsWith("/devices/inventory/node-enroll");
  const pool = onEnroll ? enrollOrgs : managed;
  const current = gateOrg(org, pool);
  if (!onEnroll && managed.length === 0 && enrollOrgs.length > 0)
    return <Navigate to="/devices/inventory/node-enroll" replace />;
  return (
    <div data-testid="inventory-tab">
      {current && <OrgCapsule current={current} candidates={pool} />}
      {/* 未歸戶｜偵測到的設備；Kinio Node 開通是設備頁自己的分頁（第二輪 R2-P2-3） */}
      {!onEnroll && managed.length > 0 && (
        // 庫存底下的兩個子分頁改成篩選膠囊（不再是第三層分頁列；第三輪 R3-P2-6）
        <div className="row chips-sub" role="tablist" aria-label={t('devtabs.inventory')}>
          <NavLink to="/devices/inventory" end role="tab" className={({ isActive }) => 'chip' + (isActive ? ' on' : '')}>
            {t("inv.tab_unassigned")}
          </NavLink>
          <NavLink to="/devices/inventory/detected" role="tab" className={({ isActive }) => 'chip' + (isActive ? ' on' : '')}>
            {t("inv.tab_detected")}
          </NavLink>
        </div>
      )}
      {!current ? (
        pool.length > 1 ? (
          <OrgChooser candidates={pool} />
        ) : (
          <p className="card muted">{t("inv.no_org")}</p>
        )
      ) : (
        <Routes>
          <Route
            path="node-enroll"
            element={<NodeEnrollPanel key={current.org_id} org={current} />}
          />
          <Route path="detected" element={<DetectedPanel org={current} />} />
          <Route path="*" element={<Unassigned org={current} />} />
        </Routes>
      )}
    </div>
  );
}

const STATUSES = ["offline", "online_unpaired", "assigned", "joined"];

function Unassigned({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { scope, scopeLabel } = useScope();
  // 範圍切換器選到這個 org 的節點 → 預設篩那個節點子樹的指派 / 綁定（可清掉）
  const scopeNode = scope.startsWith(`node:${org.org_id}:`)
    ? scope.split(":")[2]
    : "";
  // 清掉的是哪個節點：換到別的節點時又預設篩那個節點
  const [clearedNode, setClearedNode] = useState("");
  const nodeId = scopeNode && scopeNode !== clearedNode ? scopeNode : "";
  const [status, setStatus] = useState("");
  const [model, setModel] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sp, setSp] = useSearchParams();
  const [dialog, setDialogState] = useState<
    "" | "claim" | "assign" | "unassign"
  >(() => (sp.get("claim") === "1" && INVENTORY_API_READY ? "claim" : ""));
  const setDialog = (d: "" | "claim" | "assign" | "unassign") => {
    setDialogState(d);
    if (!d && sp.has("claim")) {
      const n = new URLSearchParams(sp);
      n.delete("claim");
      setSp(n, { replace: true });
    }
  };
  // 對話框開的時候固定那幾台（指派完重抓、清掉勾選後，逐台結果照樣對得上序號）
  const [picked, setPicked] = useState<InventoryDevice[]>([]);
  // 〔配對新設備〕（SPEC OT34、選單 22）：org_admin / org.devices 對任何家戶（含未指派）配對；目標 = 結構樹上的家戶；綁定不給任何人資料權
  const [pairOpen, setPairOpen] = useState(false);
  const targets = useQuery({
    queryKey: ["ot", org.org_id, "pair-targets"],
    queryFn: () => homeTargets(org),
    enabled: pairOpen,
    retry: false,
  });
  const filters = { status, model, node_id: nodeId, q: search.trim() };
  const q = useQuery({
    queryKey: ["inventory", org.org_id, filters],
    queryFn: () => inventoryApi.list(org, filters),
    enabled: INVENTORY_API_READY,
    retry: false,
    // 已指派的設備上線後會自己加入：有等加入的列就定時重抓（推論；之後改 /v1/vms/stream 的 presence 觸發）
    refetchInterval: (query) =>
      query.state.data?.devices.some((d) => d.status === "assigned")
        ? 15_000
        : false,
  });
  const rows = q.data?.devices ?? [];
  const models = useMemo(
    () => [...new Set(rows.map((d) => d.model))].sort(),
    [rows],
  );
  const { assignable, unassignable } = selectionActions(rows, selected);
  const pick = (refs: string[]) =>
    rows.filter((d) => refs.includes(d.device_ref));
  const refresh = () =>
    void qc.invalidateQueries({ queryKey: ["inventory", org.org_id] });
  const toggle = (id: string) =>
    setSelected((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allPicked =
    rows.length > 0 && rows.every((d) => selected.has(d.device_ref));
  return (
    <section className="card" data-testid="inventory">
      <div className="toolbar">
        <h2 className="section-title">
          {t("inv.title", { name: org.display_name })}
        </h2>
        <div className="spacer" />
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setPairOpen(true)}
          data-testid="inv-pair"
        >
          {t("pair.title")}
        </button>
        <button
          className="btn btn-sm"
          disabled={!INVENTORY_API_READY}
          onClick={() => setDialog("claim")}
          data-testid="inv-claim"
        >
          {t("inv.claim")}
        </button>
      </div>
      {pairOpen && targets.isLoading && (
        <p className="muted small">{t("common.loading")}</p>
      )}
      {pairOpen && targets.isError && (
        <p className="error">{errText(t, targets.error)}</p>
      )}
      <PairDialog
        open={pairOpen && !!targets.data}
        close={() => setPairOpen(false)}
        targets={targets.data}
      />
      <ClaimDialog
        org={org}
        open={dialog === "claim"}
        close={() => setDialog("")}
        done={refresh}
      />
      <AssignDialog
        org={org}
        devices={picked}
        open={dialog === "assign"}
        close={() => setDialog("")}
        done={() => (refresh(), setSelected(new Set()))}
      />
      <UnassignDialog
        org={org}
        devices={picked}
        open={dialog === "unassign"}
        close={() => setDialog("")}
        done={() => (refresh(), setSelected(new Set()))}
      />
      <p className="muted small">{t("inv.hint")}</p>
      <div className="toolbar" data-testid="inv-filters">
        <select
          className="input"
          style={{ width: "auto" }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={t("inv.col_status")}
        >
          <option value="">{t("inv.filter_all_status")}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`inv.status_${s}`)}
            </option>
          ))}
        </select>
        <select
          className="input"
          style={{ width: "auto" }}
          value={model}
          onChange={(e) => setModel(e.target.value)}
          aria-label={t("devices.col_model")}
        >
          <option value="">{t("inv.filter_all_models")}</option>
          {models.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <input
          className="input"
          style={{ width: 180 }}
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("inv.search_serial")}
          aria-label={t("inv.search_serial")}
        />
        {nodeId && (
          <span className="chip" data-testid="inv-node-filter">
            {t("inv.filter_node", { name: scopeLabel || scopeNode })}
            <button
              className="icon-btn"
              aria-label={t("inv.filter_node_clear")}
              onClick={() => setClearedNode(scopeNode)}
            >
              ×
            </button>
          </span>
        )}
        <div className="spacer" />
        <button
          className="btn btn-sm"
          disabled={assignable.length === 0}
          onClick={() => (setPicked(pick(assignable)), setDialog("assign"))}
          data-testid="inv-assign"
        >
          {t("inv.assign_to", { n: assignable.length })}
        </button>
        <button
          className="btn btn-sm"
          disabled={unassignable.length === 0}
          onClick={() => (setPicked(pick(unassignable)), setDialog("unassign"))}
          data-testid="inv-unassign"
        >
          {t("inv.unassign_n", { n: unassignable.length })}
        </button>
      </div>
      {q.isLoading && <p className="muted small">{t("common.loading")}</p>}
      {q.isError && <p className="error">{errText(t, q.error)}</p>}
      {/* 表格空的時候只一句說明（主要動作在上方），不畫空表頭（SPEC DEV-15、VMS UX 評審 2026-10-13 P1-8） */}
      {!q.isLoading && !q.isError && rows.length === 0 && (
        <p className="muted" data-testid="inv-empty">
          {t("inv.empty")}
        </p>
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label={t("inv.select_all")}
                    checked={allPicked}
                    onChange={() =>
                      setSelected(
                        allPicked
                          ? new Set()
                          : new Set(rows.map((d) => d.device_ref)),
                      )
                    }
                  />
                </th>
                <th>{t("devices.col_model")}</th>
                <th>{t("inv.col_serial")}</th>
                <th>{t("inv.col_status")}</th>
                <th>{t("devices.col_last_seen")}</th>
                <th>{t("inv.col_target")}</th>
                <th>{t("inv.col_source")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.device_ref}
                  data-inv-row={serialOrTail(d)}
                  data-status={d.status}
                >
                  <td>
                    <input
                      type="checkbox"
                      aria-label={serialOrTail(d)}
                      checked={selected.has(d.device_ref)}
                      onChange={() => toggle(d.device_ref)}
                    />
                  </td>
                  <td>
                    {d.status === "joined" && d.binding ? (
                      <Link
                        to={`/devices/${encodeURIComponent(d.binding.home_id)}/${encodeURIComponent(d.device_ref)}`}
                      >
                        {d.model}
                      </Link>
                    ) : (
                      d.model
                    )}
                  </td>
                  <td className="mono">{serialOrTail(d)}</td>
                  <td>
                    <span className="chip">
                      {t(`inv.status_${d.status}`, { defaultValue: t('common.state_unknown') })}
                    </span>
                    {d.assignment?.last_error && (
                      <div className="error small" data-testid="inv-last-error">
                        {t(`inv.err_${d.assignment.last_error}`, {
                          defaultValue: d.assignment.last_error,
                        })}
                      </div>
                    )}
                  </td>
                  <td className="small muted">
                    {d.last_seen_at
                      ? stamp(d.last_seen_at)
                      : "—"}
                  </td>
                  <td className="small">
                    {d.assignment?.home_name || d.binding?.home_name || "—"}
                  </td>
                  <td className="small muted">
                    {t(`inv.source_${d.source}`, { defaultValue: d.source })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data?.next_cursor && <p className="muted small">{t("inv.more")}</p>}
    </section>
  );
}
